import React, { useState } from 'react';
import { Box, Typography, TextField, IconButton, InputAdornment } from '@mui/material';
import { updatePassword } from 'aws-amplify/auth';
import SuccessDialog from '../../UIControls/api-button/SuccessDialog';
import EditIcon from '../../../assets/icons/EditBlue.svg';
import { ReactComponent as EyeOffIcon } from '../../../assets/icons/EyeOff.svg';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';

const SettingsPassword = ({
    isChangePasswordDialogOpen,
    setIsChangePasswordDialogOpen,
    canEdit = true,
    showEditAction = true,
    sectionSx = {},
}) => {
    const [passwordChangeError, setPasswordChangeError] = useState('');
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const renderPasswordVisibilityAdornment = () => (
        <InputAdornment position="end">
            <IconButton
                onClick={() => setShowPassword((prev) => !prev)}
                edge="end"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disableRipple
            >
                <EyeOffIcon style={{ width: 19, height: 16, opacity: showPassword ? 0.5 : 1 }} />
            </IconButton>
        </InputAdornment>
    );

    const handleChangePasswordClick = async () => {
        const currentPassword = document.getElementById('current-password').value;
        const newPassword = document.getElementById('new-password').value;
        const confirmPassword = document.getElementById('confirm-password').value;

        setPasswordChangeError('');

        if (newPassword !== confirmPassword) {
            setPasswordChangeError('Please ensure confirm password matches.');
            return false;
        }

        try {
            await updatePassword({ oldPassword: currentPassword, newPassword });
            setIsChangePasswordDialogOpen(false);
            setIsSuccessDialogOpen(true);
            return true;
        } catch (error) {
            console.error('Error changing password:', error);
            if (error.code === 'NotAuthorizedException') {
                setPasswordChangeError('Current password is incorrect.');
            } else {
                setPasswordChangeError(
                    'Failed to change password. Ensure your new password is min 8 characters long with at least 1 number, uppercase and lowercase letter',
                );
            }
            return false;
        }
    };

    return (
        <Box
            className="settings-block"
            sx={{
                backgroundColor: 'white',
                border: '1px solid rgba(102, 102, 102, 0.15)',
                padding: { xs: '20px 22px', md: '24px 30px' },
                borderRadius: '30px',
                marginBottom: '24px',
                ...sectionSx,
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                position="relative"
                sx={{ gap: 1, minWidth: 0 }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600, height: 48, flex: 1, minWidth: 0 }}>
                    Password
                </Typography>
                {showEditAction && (
                    <IconButton
                        onClick={() => canEdit && setIsChangePasswordDialogOpen(true)}
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        sx={{
                            p: 0,
                            width: 38,
                            height: 38,
                            borderRadius: '38px',
                            bgcolor: isChangePasswordDialogOpen ? '#000453' : '#FF669B',
                            '&:hover': { bgcolor: isChangePasswordDialogOpen ? '#1C2A8A' : '#FF85B0' },
                            '&:active': { bgcolor: isChangePasswordDialogOpen ? '#000453' : '#FF669B' },
                            '&:focus': { bgcolor: isChangePasswordDialogOpen ? '#000453' : '#FF669B' },
                            '&.Mui-focusVisible': { bgcolor: isChangePasswordDialogOpen ? '#000453' : '#FF669B' },
                            WebkitTapHighlightColor: 'transparent',
                            flexShrink: 0,
                        }}
                    >
                        <img src={EditIcon} alt="Edit" style={{ width: 18, height: 18 }} />
                    </IconButton>
                )}
            </Box>

            <Box
                sx={{
                    fontFamily: 'monospace',
                    fontSize: '16px',
                    lineHeight: 1.5,
                    letterSpacing: '1px',
                    mb: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'clip',
                }}
            >
                {'*'.repeat(18)}
            </Box>

            <ConfirmationDialog
                open={isChangePasswordDialogOpen}
                handleClose={() => setIsChangePasswordDialogOpen(false)}
                onConfirm={handleChangePasswordClick}
                title="Change password"
                confirmText="Change"
                cancelText="Cancel"
                message={
                    <>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="current-password"
                            label="Current Password"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            variant="outlined"
                            error={!!passwordChangeError}
                            inputProps={{ className: 'mp-mask-password' }}
                            InputProps={{
                                endAdornment: renderPasswordVisibilityAdornment(),
                            }}
                        />
                        <TextField
                            margin="dense"
                            id="new-password"
                            label="New password"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            variant="outlined"
                            inputProps={{ className: 'mp-mask-password' }}
                            InputProps={{
                                endAdornment: renderPasswordVisibilityAdornment(),
                            }}
                        />
                        <TextField
                            margin="dense"
                            id="confirm-password"
                            label="Confirm new password"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            variant="outlined"
                            inputProps={{ className: 'mp-mask-password' }}
                            InputProps={{
                                endAdornment: renderPasswordVisibilityAdornment(),
                            }}
                        />
                        {passwordChangeError && (
                            <Typography color="error" variant="body2">
                                {passwordChangeError}
                            </Typography>
                        )}
                    </>
                }
            />

            <SuccessDialog
                open={isSuccessDialogOpen}
                handleClose={() => setIsSuccessDialogOpen(false)}
                title="Password Changed"
                message="Your password has been successfully changed!"
            />
        </Box>
    );
};

export default SettingsPassword;
